import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'

export default {
    computed: {
        ...mapGetters([
            'uid',
            'survey',
            'client',
            'option',
            'year',
            'summary',
            'isAEPScheduling',
        ]),
        hasChanges () {
            return Object.keys(this.changes).length
        },
        // Whether THIS wizard's survey loads are AEP loads. The store's
        // getSurvey action otherwise infers the flag by regex-testing the
        // route for "aep", which no /renewal/* path matches — so the AEP
        // wizard was loading its survey untagged while its completion was
        // hardcoded `{ aep: true }`, splitting start and finish across two
        // different Salesforce opportunities. Overridden to true in
        // mixins/renewal-page.js; the inference still covers everything else.
        surveyIsAep () {
            return false
        }
    },
    autosave: true,
    watch: {
        'user.id' (newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                return this.$fetch()
            }
        },
    },
    async fetch () {
        this.unwatch()
        // One per page, dropped in destroyed(): a handler left on $root kept
        // the closed page alive, the Plans page with every plan card (~1 GB
        // for a big client), one more per visit until Chrome crashed the tab
        // (2026-10-22, Allison).
        if (typeof window !== 'undefined' && window.$root) {
            window.$root.$off('logout', this.unwatchOnLogout)
            window.$root.$once('logout', this.unwatchOnLogout)
        }
        let survey = null
        survey = await this.getSurvey({ id: this.uid, alert: false, aep: this.surveyIsAep })
        const preIntakeSurvey = typeof localStorage !== 'undefined' ? this.$jsonSafeParse(localStorage.getItem('pre-intake-survey') || '{}') : {}
        if (!survey?.id) {
            survey = { ...(this.form || {}), ...(survey || {}), ...preIntakeSurvey }
        }
        let syncPreIntakeSurvey = false
        if (survey && preIntakeSurvey && Object.keys(preIntakeSurvey).length) {
            const s = { ...survey, ...preIntakeSurvey }
            const appendFields = ['drugs', 'providers', 'pharmacies']
            appendFields.forEach((key) => {
                s[key] = _.uniqBy((survey[key] || []).concat(preIntakeSurvey[key] || []), item => item?.npi || item?.rxcui || item?.id)
            })
            survey = s
            syncPreIntakeSurvey = true
        }
        let iterateOn = this.form
        if (!this.form || !Object.keys(this.form).length) {
            this.$set(this, 'form', {})
            iterateOn = _.cloneDeep(survey)
        }
        if (this.iterateOnFormSurveyMerge) {
            iterateOn = { ...iterateOn, ...survey, ...this.form }
        }
        for (const field in iterateOn) {
            const initialValue = survey[field]
            // console.log(`setting field: ${field}`, 'with value', initialValue)
            // Don't let a revalidation fetch clobber an edit the user just
            // made: `field in this.changes` means there's a pending change
            // (autosave debounced up to 600ms, or the PATCH is still
            // in-flight) that hasn't been superseded by a fresher edit — a
            // server echo of the pre-edit value must not win. Checkup's
            // synchronous store hydration (mixins/checkup-page.js's
            // created()) makes this reachable in practice: the user can now
            // interact before this fetch() even resolves.
            if (!(field in this.changes)) {
                this.$set(this.form, field, _.cloneDeep(initialValue))
            }
            setTimeout(() => {
                const autosave = this.$options.autosave === true
                const handler = (newValue, oldValue) => {
                    if (this.errors.has(field) || _.isEqual(newValue, oldValue) || (_.isNil(newValue) && _.isNil(oldValue))) {
                        return
                    }
                    console.log(`change handler: ${field}`, 'from', typeof oldValue, oldValue, 'to', typeof newValue, newValue)
                    const value = _.cloneDeep(this.form[field])
                    this.$store.commit('field', { field, value })
                    this.$emit('input', value)
                    this.$set(this.changes, field, value)
                    if (autosave) {
                        return this.saveChanges()
                    }
                }
                this.watchers.push(this.$watch(`survey.${field}`, {
                    deep: true,
                    handler (newValue, oldValue) {
                        if (this.errors.has(field) || _.isEqual(newValue, oldValue) || (_.isNil(newValue) && _.isNil(oldValue))) {
                            return
                        }
                        this.$set(this.form, field, newValue)
                    }
                }))
                this.watchers.push(this.$watch(`form.${field}`, {
                    deep: true,
                    handler: autosave ? _.debounce(handler, 600) : handler
                }))
            }, 300)
        }
        if (syncPreIntakeSurvey) {
            for (const field in preIntakeSurvey) {
                const value = survey[field]
                this.$store.commit('field', { field, value })
                this.$emit('input', value)
                this.$set(this.changes, field, value)
                this.$set(this.form, field, value)
            }
            if (survey?.id) {
                await this.saveChanges()
                localStorage.removeItem('pre-intake-survey')
            }
        }

        await this.postSurveyFetch(survey)
        this.$nextTick(() => {
            this.lazyPostSurveyFetch(survey)
        })
    },
    data () {
        return {
            watchers: [],
            changes: {},
            form: {}
        }
    },
    destroyed () {
        this.unwatch()
        if (typeof window !== 'undefined' && window.$root) {
            window.$root.$off('logout', this.unwatchOnLogout)
        }
    },
    methods: {
        ...mapActions([
            'getSurvey',
            'completeSurvey'
        ]),
        unwatch () {
            if (this.watchers && this.watchers.length) {
                this.watchers.forEach(watcher => watcher())
            }
        },
        unwatchOnLogout () {
            console.log('logout event received, unwatching survey changes')
            this.unwatch()
        },
        postSurveyFetch () {},
        lazyPostSurveyFetch () {},
        async saveChanges () {
            if (!this.isAgentMode) {
                this.$store.commit('autosave', 'saving')
            }
            if (this.hasChanges) {
                this.$store.commit('autosave', 'saving')
                console.log('saving fields', Object.keys(this.changes))
                // triggers for survey
                const triggers = [null, 'va', 'fep_blue', 'tricare', 'champva']
                const changedKey = Object.keys(this.changes)[0]
                const changedValue = Object.values(this.changes)[0]
                if (triggers.includes(changedKey) && !!changedValue) {
                    const index = triggers.indexOf(changedKey)
                    const scritpTabIndex = index === triggers.length - 1 ? triggers.length - 2 : index
                    this.$store.commit('setActiveSurveyTab', 2)
                    this.$store.commit('setActiveScriptTab', scritpTabIndex)
                    this.$store.commit('support', true)
                }
                if (this.$store.state.survey?.id) {
                    const changesClone = _.cloneDeep(this.changes)
                    const resp = await this.$try('updateSurvey', {
                        id: this.uid,
                        // Server-owned echoes, never written back: `agent` is
                        // the advisor object the header badge commits after it
                        // has PATCHed agent_id itself (FLOW-FRONTEND-9J-5SQ).
                        data: _.omit(changesClone, 'updated', 'crm_id', 'agent')
                    },
                    { spinner: false }
                    )
                    // A canceled request (a newer autosave superseded it) comes
                    // back undefined. The newer one owns the outcome, so this is
                    // neither saved nor errored (FLOW-FRONTEND-9J-2JW).
                    if (!resp) return false
                    const { error } = resp
                    if (error) {
                        this.$store.commit('autosave', 'errored')
                        return false
                        // throw error?
                    } else {
                        this.$store.commit('autosave', 'saved')
                        this.changes = {}
                        for (const key in changesClone) {
                            this.$emit('form:saved', { key, value: changesClone[key] })
                        }
                        return true
                    }
                }
                this.changes = {}
            } else if (!this.isAgentMode) {
                setTimeout(() => {
                    this.$store.commit('autosave', 'saved')
                }, 500)
            }
            return false
        },
        updateFormFromSurveyModel (surveyModel, questions) {
            questions.forEach((q) => {
                const name = q?.propertyHash?.name
                const question = surveyModel.getQuestionByName(name)
                let value = _.cloneDeep(question.value)

                // this is dumb, surveyjs treats empty string as undefined, so if your API doesn't process them, you can't clear a value
                // https://github.com/surveyjs/survey-library/blob/b9657e9d2683e4aa600600c053b42dfcc9326c84/src/survey.ts#L5622-L5623
                if (value === undefined && question.getType() === 'text') {
                    value = ''
                }
                const pick = question?.propertyHash?.pick ? question?.propertyHash?.pick.split(',') : []
                if (pick?.length && value && _.isObject(value)) {
                    _.forEach(pick, (k) => {
                        if (this.form[k] !== value[k]) {
                            this.$set(this.form, k, value[k])
                        }
                    })
                } else if (!_.isEqual(this.form[name], value)) {
                    this.$set(this.form, name, value)
                }
            })
        }
    },
}
